<template>
  <v-flex xs12>
    <data-source-table
      itemController="microsite"
      itemIdentifyField="title"
      :headers="headers"
      :options.sync="options"
      :selected-items.sync="selectedItems"
      :get-data-hook="getDataHook"
      :add-item-hook="addItemHook"
      ref="dataSourceRef"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <confirm-dialog
            :is-open.sync="dialogs.createNewDialog.isOpened"
            :is-disable-confirm-button="dialogs.createNewDialog.data.title.length===0"
            width="400"
            confirm-button-text="Add"
            @confirm="saveNew"
          >
            <template v-slot:title>
              Create new microsite
            </template>
            <template v-slot:body>
              <v-row>
                <v-col
                  cols="12"
                >
                  <v-text-field
                    v-model="dialogs.createNewDialog.data.title"
                    label="Microsite title"
                    autofocus
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="6"
                >
                  <GeotagList
                    :geotag-model.sync="dialogs.createNewDialog.data.geotags"
                    label="geotags"
                    multiple
                    return-object
                  />
                </v-col>
                <v-col
                  cols="6"
                >
                  <DataList
                    :item-model.sync="dialogs.createNewDialog.data.customers"
                    :type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
                    label="customers"
                    multiple
                    clearable
                    chips
                    small-chips
                    deletable-chips
                    return-object
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                >
                  <DataList
                    :item-model.sync="dialogs.createNewDialog.data.customer_owner_id"
                    :type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
                    label="owner customer (empty = global)"
                    clearable
                    dense
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="4"
                >
                  <v-select
                    label="Priority"
                    :value="dialogs.createNewDialog.data.priority"
                    @change="v=>dialogs.createNewDialog.data.priority=v"
                    :items="priorities"
                  />
                </v-col>
                <v-col
                  cols="4"
                >
                  <v-switch
                    class="mt-3"
                    dense
                    label="Enabled"
                    v-model="dialogs.createNewDialog.data.is_enabled"
                  />
                </v-col>
                <v-col
                  cols="4"
                >
                  <v-switch
                    class="mt-3"
                    dense
                    label="Sponsored"
                    v-model="dialogs.createNewDialog.data.is_sponsored"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <div>Platform</div>
                  <v-btn-toggle
                      v-model="dialogs.createNewDialog.data.platform"
                      dense
                  >
                    <v-btn
                        v-for="(platform, index) in platforms"
                        :key="index"
                        active-class="green darken-4 green--text text--lighten-4"
                    >
                      {{ platform }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </template>
          </confirm-dialog>

          <confirm-dialog
            :is-open.sync="dialogs.bulkAddGeotagsDialog.isOpened"
            width="400"
            confirm-button-text="Add"
            @cancel="closeDialog('bulkAddGeotagsDialog')"
            @confirm="updateBulkGeotags"
          >
            <template v-slot:title>
              Add geotags to {{
                selectedItems.length === 1
                  ? `microsite ${selectedItems[0].title}`
                  : `${selectedItems.length} microsites`
              }}
            </template>
            <template v-slot:body>
              <GeotagList
                :geotag-model.sync="dialogs.bulkAddGeotagsDialog.data.geotags"
                multiple
                dense
                return-object
              />
            </template>
          </confirm-dialog>

          <confirm-dialog
            :is-open.sync="dialogs.bulkAddCustomersDialog.isOpened"
            width="400"
            confirm-button-text="Add"
            @cancel="closeDialog('bulkAddCustomersDialog')"
            @confirm="updateBulkCustomers"
          >
            <template v-slot:title>
              Add customers to {{
                selectedItems.length === 1
                  ? `microsite ${selectedItems[0].title}`
                  : `${selectedItems.length} microsites`
              }}
            </template>
            <template v-slot:body>
              <DataList
                :item-model.sync="dialogs.bulkAddCustomersDialog.data.customers"
                :type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
                label="customers"
                clearable
                multiple
                dense
                chips
                small-chips
                deletable-chips
                return-object
              />
            </template>
          </confirm-dialog>

          <confirm-dialog
            :is-open.sync="dialogs.bulkEnableDialog.isOpened"
            :is-disable-confirm-button="dialogs.bulkEnableDialog.data.isEnabled === null"
            width="400"
            :confirm-button-text="bulkEnableConfirmText"
            @cancel="closeDialog('bulkEnableDialog')"
            @confirm="updateBulkEnabled"
          >
            <template v-slot:title>
              Enable or disable {{
                selectedItems.length === 1
                  ? `microsite ${selectedItems[0].title}`
                  : `${selectedItems.length} microsites`
              }}
            </template>
            <template v-slot:body>
              <v-row>
                <v-btn-toggle
                  v-model="dialogs.bulkEnableDialog.data.isEnabled"
                >
                  <v-btn :value="true">Enable</v-btn>
                  <v-btn :value="false">Disable</v-btn>
                </v-btn-toggle>
                <div
                  class="ml-5"
                >
                  <div>
                    {{ selectedEnabledNumber }} {{ selectedEnabledNumber > 1 ? 'are' : 'is' }} enabled
                  </div>
                  <div>
                    {{ selectedDisabledNumber }} {{ selectedDisabledNumber > 1 ? 'are' : 'is' }} disabled
                  </div>
                </div>
              </v-row>
            </template>
          </confirm-dialog>

          <v-btn
            class="mx-2"
            color="primary"
            dark
            fab
            small
            @click="openDialog('createNewDialog')"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  dark
                >
                  mdi-plus
                </v-icon>
              </template>
              create new microsite
            </v-tooltip>
          </v-btn>

          <v-btn
            class="mx-2"
            color="primary"
            fab
            small
            :disabled="isDisabledBulk"
            @click="openDialog('bulkAddGeotagsDialog', { micrositesIds: selectedItems})"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  dark
                >
                  mdi-city-variant
                </v-icon>
              </template>
              bulk add geotag
            </v-tooltip>
          </v-btn>

          <v-btn
            class="mx-2"
            color="primary"
            fab
            small
            :disabled="isDisabledBulk"
            @click="openDialog('bulkAddCustomersDialog')"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  dark
                >
                  mdi-bank-plus
                </v-icon>
              </template>
              bulk add customers
            </v-tooltip>
          </v-btn>

          <v-btn
            class="mx-2"
            color="primary"
            fab
            small
            :disabled="isDisabledBulk"
            @click="openDialog('bulkEnableDialog')"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  dark
                >
                  mdi-eye-outline
                </v-icon>
              </template>
              bulk enable
            </v-tooltip>
          </v-btn>

          <confirm-dialog
              :is-open.sync="dialogs.updateCustomerMicrosites.isOpened"
              width="400"
              confirm-button-text="Add"
              @cancel="closeDialog('updateCustomerMicrosites')"
              @confirm="updateCustomerMicrosites"
          >
            <template v-slot:activator>
              <v-btn
                  class="mx-2" color="cyan darken-3"
                  dark fab small
                  @click="openDialog('updateCustomerMicrosites')"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <slot>
                      <v-icon v-bind="attrs" v-on="on" dark>
                        mdi-account-arrow-up
                      </v-icon>
                    </slot>
                  </template>
                  update customer microsites
                </v-tooltip>
              </v-btn>
            </template>
            <template v-slot:title>
              Update Customer Microsites
            </template>
            <template v-slot:body>
              <v-select
                  label="Customer"
                  v-model="dialogs.updateCustomerMicrosites.data.customer"
                  :items="['UAL']"
              />
            </template>
          </confirm-dialog>

          <confirm-dialog
              :is-open.sync="dialogs.resultCustomerMicrosites.isOpened"
              width="400"
              cancelButtonText="Ok"
              :showConfirmButton="false"
              @cancel="closeResultCustomerMicrosites()"
          >
            <template v-slot:title>
              Result Update Customer Microsites
            </template>
            <template v-slot:body>
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr
                        v-for="(item, key) in dialogs.resultCustomerMicrosites.data.result"
                        :key="key"
                    >
                      <td>{{ key }}</td>
                      <td>{{ item }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
          </confirm-dialog>

          <confirm-dialog
              :is-open.sync="dialogs.additionalInfo.isOpened"
              width="400"
              :showConfirmButton="false"
              @cancel="closeDialog('additionalInfo')"
          >
            <template v-slot:title>
              Additional info
            </template>
            <template v-slot:body>
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                  <tr
                      v-for="(item, key) in dialogs.additionalInfo.data.result"
                      :key="key"
                  >
                    <td>{{ key }}</td>
                    <td>{{ item }}</td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
          </confirm-dialog>

          <v-spacer/>

          <v-btn
              class="mx-2" color="blue darken-1"
              outlined small
              @click="$refs.dataSourceRef ? $refs.dataSourceRef.openDialogSelectColumns() : false"
          >
            Select columns
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:filter_geotags>
        <GeotagList
          :geotag-model.sync="options.filters.geotags"
          label="geotags"
          multiple
          dense
        />
      </template>
      <template v-slot:filter_customers>
        <DataList
          :item-model.sync="options.filters.customers"
          :type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
          label="customers"
          clearable
          multiple
          dense
          chips
          small-chips
          deletable-chips
          with-option-none
        />
      </template>
      <template v-slot:filter_customer_owner>
        <DataList
          :item-model.sync="options.filters.customer_owner"
          :type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
          label="owner"
          clearable
          multiple
          dense
          chips
          small-chips
          deletable-chips
          with-option-none
        />
      </template>

      <template v-slot:item.title="{ item }">
        <v-edit-dialog
          @save="updateMicrosite('title', clonedItem.title, item)"
          @open="clonedItem = {...item}"
        >
          <div v-if="item.title" class="font-weight-black">
            {{ item.title }}
            <OpenItemPage
              item-route="microsites"
              :item-id="item.id"
              tooltip-text="open microsite page"
            />
          </div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"></div>
          <template v-slot:input>
            <v-text-field
              v-model="clonedItem.title"
              label="Edit"
              single-line
              counter
            />
          </template>
        </v-edit-dialog>

        <translations-bar
          :id="item.id"
          :translations="translationsTitle[item.id] ? translationsTitle[item.id] : {}"
          :model="$constants.TRANSLATION_MODELS.MICROSITE_TITLE"
          :title="item.title"
          :trigger-update="item.title"
        >
        </translations-bar>
      </template>

      <template v-slot:item.geotags="{ item }">
        <EditableList
          label="geotag"
          :object="item"
          :values="item.geotags"
          :list-type-object="$constants.DATA_LISTS.GEOTAG_LIST"
          :multiple="true"
          description-field="country_state"
          object-name-field="title"
          @addItem="addGeotags"
          @removeItem="removeGeotag"
        />
      </template>

      <template v-slot:item.customers="{ item }">
        <EditableList
          label="customer"
          :object="item"
          :values="item.customers"
          :list-type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
          :multiple="true"
          object-name-field="title"
          @addItem="addCustomers"
          @removeItem="removeCustomer"
        />
      </template>

      <template v-slot:item.customer_owner="{ item }">
        <EditableList
          label="owner"
          action-verb="set"
          :object="item"
          :values="item.owner ? [item.owner] : []"
          :list-type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
          :multiple="false"
          object-name-field="title"
          @addItem="setOwner"
          @removeItem="removeOwner"
        />
      </template>

      <template v-slot:item.platform="{ item }">
        <v-btn-toggle
            v-model="item.platform"
            dense
        >
          <v-btn
              v-for="(platform, index) in platforms"
              :key="index"
              active-class="green darken-4 green--text text--lighten-4"
              @click="changePlatform(item, index)"
          >
            {{ platform }}
          </v-btn>
        </v-btn-toggle>
      </template>

      <template v-slot:item.additional_info="{ item }">
        <v-btn
            v-if="item.additional_info"
            class="mx-2" color="cyan darken-3" icon small
            @click="openDialog('additionalInfo', { result: item.additional_info })"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <slot>
                <v-icon v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </slot>
            </template>
            show additional info
          </v-tooltip>
        </v-btn>
      </template>
    </data-source-table>
  </v-flex>
</template>

<script>
import { mapActions } from 'vuex';
import ConfirmDialog from '../components/dialogs/ConfirmDialog.vue';
import EditableList from '../components/EditableList.vue';
import TranslationsBar from '../components/Translations/TranslationsBar.vue';
import manageDialogsMixin from '../mixins/manageDialogsMixin';
import OpenItemPage from '../components/OpenItemPage.vue';
import DataSourceTable from '../components/DataSource/DataTable.vue';
import GeotagList from '../components/DataLists/GeotagList.vue';
import DataList from '../components/DataLists/DataList.vue';

export default {
  name: 'Microsites',
  components: {
    DataList,
    GeotagList,
    DataSourceTable,
    TranslationsBar,
    ConfirmDialog,
    EditableList,
    OpenItemPage,
  },

  mixins: [
    manageDialogsMixin,
  ],

  data() {
    const prioritiesFilter = [1, 2, 3, 4, 'empty'];
    const priorities = [1, 2, 3, 4];
    const platforms = ['all', 'web', 'native'];
    return {
      clonedItem: {},
      dialogs: {
        createNewDialog: {
          isOpened: false,
          data: {
            title: '',
            priority: null,
            geotags: [],
            customers: [],
            customer_owner_id: null,
            is_enabled: true,
            is_sponsored: false,
            platform: 0,
          },
          defaults: {
            title: '',
            priority: null,
            geotags: [],
            customers: [],
            customer_owner_id: null,
            is_enabled: true,
            is_sponsored: false,
            platform: 0,
          },
        },
        bulkAddCustomersDialog: {
          isOpened: false,
          data: {
            micrositesIds: [],
            customers: [],
          },
          defaults: {
            micrositesIds: [],
            customers: [],
          },
        },
        bulkAddGeotagsDialog: {
          isOpened: false,
          data: {
            micrositesIds: [],
            geotags: [],
          },
          defaults: {
            micrositesIds: [],
            geotags: [],
          },
        },
        bulkEnableDialog: {
          isOpened: false,
          data: {
            micrositesIds: [],
            isEnabled: null,
          },
          defaults: {
            micrositesIds: [],
            isEnabled: null,
          },
        },
        updateCustomerMicrosites: {
          isOpened: false,
          data: {
            customer: null,
          },
          defaults: {
            customer: null,
          },
        },
        resultCustomerMicrosites: {
          isOpened: false,
          data: {
            result: {},
          },
          defaults: {
            result: {},
          },
        },
        additionalInfo: {
          isOpened: false,
          data: {
            result: {},
          },
          defaults: {
            result: {},
          },
        },
      },

      selectedItems: [],
      options: { filters: {} },
      headers: [
        {
          text: 'id', value: 'id', width: '80px',
        },
        {
          text: 'title', value: 'title',
        },
        {
          text: 'shortcut', value: 'shortcut', edit: { type: 'text', emptyPlaceholder: true },
        },
        {
          text: 'priority',
          value: 'priority',
          filter: { type: 'multipleSelect', items: prioritiesFilter },
          edit: { type: 'select', items: priorities },
        },
        {
          text: 'geotags', value: 'geotags', sortable: false,
        },
        {
          text: 'customers', value: 'customers', sortable: false,
        },
        {
          text: 'owner', value: 'customer_owner', sortable: false,
        },
        {
          text: 'enabled', value: 'is_enabled', filter: { type: 'select', items: ['on', 'off'] }, edit: { type: 'switch' },
        },
        {
          text: 'sponsored', value: 'is_sponsored', filter: { type: 'select', items: ['on', 'off'] }, edit: { type: 'switch' },
        },
        {
          text: 'platform', value: 'platform', filter: { type: 'select', items: platforms },
        },
        {
          text: 'info', value: 'additional_info', width: '80px', filter: { type: 'select', items: ['yes', 'no'] },
        },
        {
          text: '', value: 'deleteItem', width: '80px', sortable: false,
        },
      ],
      translationsTitle: {},

      priorities,
      platforms,
    };
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    getDataHook() {
      this.getTranslations();
    },
    saveNew() {
      this.$refs.dataSourceRef.saveNew(this.dialogs.createNewDialog.data)
        .finally(() => {
          this.closeDialog('createNewDialog');
        });
    },
    addItemHook(item) {
      this.getTranslations([item.id]);
    },
    updateMicrosite(field, value, item) {
      this.$refs.dataSourceRef.updateItemField(field, value, item);
    },
    updateBulk(itemIds, itemFields) {
      return this.$refs.dataSourceRef.updateBulk(itemIds, itemFields);
    },
    remove(item) {
      return this.$refs.dataSourceRef.remove(item);
    },

    getTranslations(ids = []) {
      this.$requestHelpers.post(
        'api/admin/bulk-translations',
        {
          model: this.$constants.TRANSLATION_MODELS.MICROSITE_TITLE,
          ids: ids.length === 0 ? this.$refs.dataSourceRef.allItems().map((item) => item.id) : ids,
        },
      ).then((response) => {
        if (ids.length === 0) {
          this.translationsTitle = response.data.result;
        } else {
          this.translationsTitle = {
            ...this.translationsTitle,
            ...response.data.result,
          };
        }
      });
    },

    addGeotags(item, geotags) {
      if (!geotags) return;

      this.updateMicrosite('add_geotags', geotags, item);
    },
    removeGeotag(item, geotag) {
      this.updateMicrosite('remove_geotag', geotag, item);
    },

    addCustomers(item, customers) {
      if (!customers) return;

      this.updateMicrosite('add_customers', customers, item);
    },
    removeCustomer(item, customer) {
      this.updateMicrosite('remove_customer', customer, item);
    },

    setOwner(item, customer) {
      this.updateMicrosite('customer_owner_id', customer ? customer.id : null, item);
    },
    removeOwner(item) {
      this.setOwner(item, null);
    },

    changePlatform(item, platform) {
      this.updateMicrosite('platform', platform, item);
    },

    updateBulkGeotags() {
      if (!this.selectedItems.length || !this.dialogs.bulkAddGeotagsDialog.data.geotags) {
        this.closeDialog('bulkAddGeotagsDialog');
        return false;
      }

      this.updateBulk(
        this.selectedItems.map((item) => item.id),
        { add_geotags: this.dialogs.bulkAddGeotagsDialog.data.geotags },
      ).finally(() => {
        this.closeDialog('bulkAddGeotagsDialog');
        this.$refs.dataSourceRef.clearSelectedItems();
      });

      return true;
    },

    updateBulkCustomers() {
      if (!this.selectedItems.length || !this.dialogs.bulkAddCustomersDialog.data.customers) {
        this.closeDialog('bulkAddCustomersDialog');

        return false;
      }

      this.updateBulk(
        this.selectedItems.map((item) => item.id),
        { add_customers: this.dialogs.bulkAddCustomersDialog.data.customers },
      ).finally(() => {
        this.closeDialog('bulkAddCustomersDialog');
        this.$refs.dataSourceRef.clearSelectedItems();
      });

      return true;
    },

    updateBulkEnabled() {
      if (!this.selectedItems.length || this.dialogs.bulkEnableDialog.data.isEnabled === null) {
        this.closeDialog('bulkEnableDialog');

        return false;
      }

      this.updateBulk(
        this.selectedItems.map((item) => item.id),
        { is_enabled: this.dialogs.bulkEnableDialog.data.isEnabled },
      ).finally(() => {
        this.closeDialog('bulkEnableDialog');
        this.$refs.dataSourceRef.clearSelectedItems();
      });

      return true;
    },

    updateCustomerMicrosites() {
      if (!this.dialogs.updateCustomerMicrosites.data.customer) {
        return false;
      }

      this.$requestHelpers.post(
        'api/admin/microsite/updateCustomerMicrosite',
        this.dialogs.updateCustomerMicrosites.data,
      ).then((response) => {
        this.$refs.dataSourceRef.getApiData();
        this.closeDialog('updateCustomerMicrosites');

        this.openDialog('resultCustomerMicrosites', { result: response.data.result });
      });

      return true;
    },

    closeResultCustomerMicrosites() {
      window.location.reload();
    },
  },

  computed: {
    isDisabledBulk() {
      return this.selectedItems.length === 0;
    },

    selectedDisabledNumber() {
      return this.selectedItems.reduce((acc, el) => (!el.is_enabled ? acc + 1 : acc), 0);
    },

    selectedEnabledNumber() {
      return this.selectedItems.reduce((acc, el) => (el.is_enabled ? acc + 1 : acc), 0);
    },

    bulkEnableConfirmText() {
      if (this.getDialogData('bulkEnableDialog', 'isEnabled') === null) {
        return 'Save';
      }
      return this.getDialogData('bulkEnableDialog', 'isEnabled') ? 'Enable' : 'Disable';
    },
  },
};
</script>
